import React, { useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import './AvatarCarouselPanel.css';
import { DatabasePhoto } from '../../../../types';
import { usePanelAnimation } from '../../../../hooks/useElementAnimations';
import { useSeasonsStore } from '../../../../stores/useSeasonsStore';
import { convertEventPhotosToDisplayData, convertStaticPhotosToDisplayData, convertDatabasePhotosToPhotos } from '../../../../utils/dataAdapter';
import EventPhotoGrid from '../../gallery/PhotoGrid/EventPhotoGrid';

const PANEL_SCROLL_DELAY_MS = 100;

interface AvatarCarouselPanelProps {
    isOpen: boolean;
    event: string | null;
    photos: DatabasePhoto[];
}

const AvatarCarouselPanel: React.FC<AvatarCarouselPanelProps> = ({
    isOpen,
    event,
    photos
}) => {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const { photosByEvent, isLoading } = useSeasonsStore();

    // No tap gesture: framer would make the panel wrapper a dead focus stop (see AvatarBubble).
    const { motionProps } = usePanelAnimation({ isOpen, enableTap: false });

    // Create unified photo data that uses database order when available, with proper loading handling
    const unifiedPhotoData = useMemo(() => {
        if (!event) {
            return [];
        }

        const dbEventData = photosByEvent[event];

        if (dbEventData) {
            if (dbEventData.photos.length > 0) {
                return convertEventPhotosToDisplayData(dbEventData);
            }

            // AddPhotoButton still shows because the event exists.
            return [];
        }

        // Fallback for static photos (shouldn't happen with new architecture)
        if (!isLoading && photos.length > 0) {
            const convertedPhotos = convertDatabasePhotosToPhotos(photos);
            return convertStaticPhotosToDisplayData(convertedPhotos, event, photosByEvent);
        }

        return [];
    }, [event, photosByEvent, photos, isLoading]);

    useEffect(() => {
        if (isOpen && event && panelRef.current) {
            const timer = setTimeout(() => {
                panelRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, PANEL_SCROLL_DELAY_MS);
            return () => clearTimeout(timer);
        }
    }, [isOpen, event]);

    if (!isOpen || !event) {
        return null;
    }

    return (
        <motion.div
            ref={panelRef}
            className="avatar-carousel-panel"
            {...motionProps}
        >
            <div className="avatar-carousel-panel-content">
                {isLoading && unifiedPhotoData.length === 0 ? (
                    // Show loading state to prevent layout shift
                    <div className="photo-grid-loading">
                        <div className="loading-message">Loading photos...</div>
                    </div>
                ) : (
                    <EventPhotoGrid
                        images={unifiedPhotoData}
                        photoData={unifiedPhotoData}
                        eventName={event}
                    />
                )}
            </div>
        </motion.div>
    );
};

export default AvatarCarouselPanel;
